/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import * as shrApi from '../../services/shr.api';

const BTN = 'bg-[#039089] hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-xl text-xs disabled:opacity-50';
const BTN2 = 'bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold py-2 px-4 rounded-xl text-xs disabled:opacity-50';
const INP = 'w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm';

const STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: 'Not Started',
  CONSENT_PENDING: 'Consent Pending',
  CONSENT_APPROVED: 'Consent Approved',
  CONSENT_REJECTED: 'Consent Rejected',
  VISIT_OPEN: 'Visit Open',
  TOKEN_EXPIRED: 'Token Expired',
  VISIT_CLOSED: 'Visit Closed',
  FAILED: 'Failed',
  READY_TO_SYNC: 'Ready to Sync',
  SYNCING: 'Syncing',
  SUBMITTED: 'Bundle accepted/queued',
  VERIFICATION_PENDING: 'Verification pending',
  SYNCED: 'Verified',
};

export default function ShrPanel({
  patientId, encounterId, admissionId, emergencyCaseId, visitType, compact,
}: {
  patientId: string;
  encounterId?: string;
  admissionId?: string;
  emergencyCaseId?: string;
  visitType?: string;
  compact?: boolean;
}) {
  const [consent, setConsent] = useState<any>(null);
  const [otp, setOtp] = useState('');
  const [tab, setTab] = useState<'consent' | 'history' | 'sync'>('consent');
  const [history, setHistory] = useState<any>(null);
  const [histCat, setHistCat] = useState('Encounter,Condition');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [incapacity, setIncapacity] = useState('');
  const [repCr, setRepCr] = useState('');
  const [repRel, setRepRel] = useState('');

  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      const r = await shrApi.shrGetConsent(patientId, encounterId);
      setConsent(r?.data || null);
    } catch { setConsent(null); }
  }, [patientId, encounterId]);

  useEffect(() => { load(); }, [load]);

  const st = consent?.status || 'NOT_STARTED';
  const canRequest = ['NOT_STARTED', 'CONSENT_REJECTED', 'FAILED', 'VISIT_CLOSED'].includes(st) || !consent;
  const canVerify = st === 'CONSENT_PENDING';
  const canRead = ['VISIT_OPEN', 'CONSENT_APPROVED', 'READY_TO_SYNC', 'SUBMITTED', 'VERIFICATION_PENDING'].includes(st);
  const canRefresh = canRead || st === 'TOKEN_EXPIRED';
  const canClose = canRead;

  const request = async () => {
    setBusy(true);
    try {
      const r = await shrApi.shrRequestConsent({
        patientId, encounterId, admissionId, emergencyCaseId,
        visitType: visitType || (admissionId ? 'INPATIENT' : emergencyCaseId ? 'EMERGENCY' : 'OUTPATIENT'),
        emergency, incapacityReason: incapacity || undefined,
        representativeCrId: repCr || undefined,
        representativeRelationship: repRel || undefined,
        patientCapable: emergency || repCr ? false : true,
      });
      setConsent(r.data);
      notify('SHR consent requested. Patient should receive OTP (not SHA OTP).');
    } catch (e: any) {
      notify(e?.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  const verify = async () => {
    if (!consent?.id) return;
    setBusy(true);
    try {
      const r = await shrApi.shrVerifyOtp(consent.id, otp);
      setConsent(r.data);
      setOtp('');
      notify('SHR consent approved — visit open.');
    } catch (e: any) {
      notify(e?.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  const loadHistory = async (resources = histCat) => {
    setBusy(true);
    try {
      const r = await shrApi.shrPatientRecords(patientId, resources);
      setHistory(r.data);
      setTab('history');
    } catch (e: any) {
      notify(e?.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  const sync = async () => {
    if (!encounterId) return notify('Encounter id required to sync');
    setBusy(true);
    try {
      const r = await shrApi.shrSyncEncounter({
        patientId, encounterId, admissionId, emergencyCaseId, visitType,
      });
      notify(r.data?.message || 'SHR bundle accepted/queued');
      setTab('sync');
    } catch (e: any) {
      notify(e?.response?.data?.message || (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors.join('; ') : e.message));
    }
    setBusy(false);
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 ${compact ? 'p-3' : 'p-4'} space-y-3`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-bold text-slate-800 text-sm">Kenya SHR (clinical record)</p>
          <p className="text-[11px] text-slate-400">Separate from SHA claims / eligibility</p>
        </div>
        <span className="text-xs font-bold px-2 py-1 rounded-full bg-teal-50 text-teal-800">{STATUS_LABEL[st] || st}</span>
      </div>
      {msg && <p className="text-xs text-slate-700 bg-slate-50 rounded-lg p-2">{msg}</p>}

      <div className="flex gap-1 text-[11px] font-bold">
        {(['consent', 'history', 'sync'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-2 py-1 rounded-lg ${tab === t ? 'bg-[#039089] text-white' : 'bg-slate-100 text-slate-600'}`}>{t}</button>
        ))}
      </div>

      {tab === 'consent' && (
        <div className="space-y-2">
          {canRequest && (
            <>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={emergency} onChange={e => setEmergency(e.target.checked)} /> Emergency consent (patient cannot OTP)</label>
              {emergency && <input className={INP} placeholder="Incapacity reason" value={incapacity} onChange={e => setIncapacity(e.target.value)} />}
              <input className={INP} placeholder="Representative CR ID (optional)" value={repCr} onChange={e => setRepCr(e.target.value)} />
              {repCr && <input className={INP} placeholder="Relationship" value={repRel} onChange={e => setRepRel(e.target.value)} />}
              <button disabled={busy} onClick={request} className={BTN}>Request SHR Consent</button>
            </>
          )}
          {canVerify && (
            <div className="flex gap-2">
              <input className={INP} placeholder="OTP" value={otp} onChange={e => setOtp(e.target.value)} />
              <button disabled={busy} onClick={verify} className={BTN}>Verify OTP</button>
              <button disabled={busy} onClick={() => shrApi.shrResendOtp(consent.id).then(() => notify('OTP resent'))} className={BTN2}>Resend</button>
            </div>
          )}
          {canRefresh && consent?.id && <button disabled={busy} onClick={() => shrApi.shrRefreshVisit(consent.id).then(r => { setConsent(r.data); notify('Consent refreshed'); })} className={BTN2}>Refresh consent</button>}
          {canClose && consent?.id && <button disabled={busy} onClick={() => shrApi.shrCloseVisit(consent.id).then(r => { setConsent(r.data); notify('SHR visit closed'); })} className={BTN2}>Close SHR visit</button>}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2">
          {!canRead && <p className="text-xs text-amber-800">Open SHR visit + consent required to read national records.</p>}
          <div className="flex flex-wrap gap-1">
            {['Encounter,Condition', 'Observation,Condition', 'MedicationRequest,MedicationDispense', 'Procedure,Immunization', 'AllergyIntolerance'].map(r => (
              <button key={r} disabled={!canRead || busy} onClick={() => { setHistCat(r); loadHistory(r); }} className={BTN2}>{r.split(',')[0]}</button>
            ))}
          </div>
          {history?.items && (
            <div className="max-h-48 overflow-y-auto text-xs space-y-1">
              <p className="font-bold text-indigo-700">Source: KENYA SHR</p>
              {(history.items || []).slice(0, 40).map((it: any, i: number) => {
                const res = it.resource || it;
                return <p key={i} className="border-b border-slate-100 py-1 font-mono">{res.resourceType || 'Resource'} {res.id || ''} {res.code?.text || res.vaccineCode?.text || ''}</p>;
              })}
              {history.nextPageToken && <button className={BTN2} onClick={async () => {
                const r = await shrApi.shrPatientRecords(patientId, histCat, history.nextPageToken);
                setHistory({ ...r.data, items: [...(history.items || []), ...(r.data.items || [])], nextPageToken: r.data.nextPageToken });
              }}>Next page</button>}
            </div>
          )}
        </div>
      )}

      {tab === 'sync' && (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-500">HMIS remains source of truth. Successful HTTP does not mean the national record is already persisted.</p>
          <button disabled={busy || !canRead} onClick={sync} className={BTN}>Sync encounter to SHR</button>
        </div>
      )}
    </div>
  );
}
